import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  InputBase,
  Divider,
  Chip,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import { FormField } from "./FormControl";

import { BookingData } from "../../../../../types/bookings";
import apiService from "../../../../../service/Api";

// ── Nominatim types ──────────────────────────────────────────────────────────

interface NominatimAddress {
  road?: string;
  house_number?: string;
  suburb?: string;
  neighbourhood?: string;
  city?: string;
  town?: string;
  village?: string;
  county?: string;
  state?: string;
  postcode?: string;
}

interface NominatimResult {
  place_id: string;
  display_name: string;
  name?: string;
  address: NominatimAddress;
}

// ── Suggestion item shape (unified across both sources) ──────────────────────

interface VenueSuggestion {
  id: string;
  source: "backend" | "nominatim";
  displayName: string;
  subtext: string;
  // resolved address fields
  venue_name: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
}

// ── Fill state ───────────────────────────────────────────────────────────────

type FillSource = "backend" | "nominatim" | "manual" | null;

// ── Props ────────────────────────────────────────────────────────────────────

interface Props {
  bookingData: BookingData;
  setBookingData: React.Dispatch<React.SetStateAction<BookingData>>;
}

// ── Parse Nominatim result ───────────────────────────────────────────────────

function parseNominatim(
  result: NominatimResult,
): Omit<VenueSuggestion, "id" | "source" | "displayName" | "subtext"> {
  const a = result.address;
  const addressParts = [a.house_number, a.road].filter(Boolean);

  const address_one = addressParts.join(" ") || "";

  const address_two = a.neighbourhood ?? a.postcode ?? "";
  const suburb = a.suburb ?? a.county ?? "";
  const city = a.city ?? a.town ?? a.village ?? a.state ?? "";
  const venue_name =
    result.name && result.name !== result.display_name
      ? result.name
      : result.display_name.split(",")[0].trim();

  return { venue_name, address_one, address_two, suburb, city };
}

// ── Component ────────────────────────────────────────────────────────────────

export function ServiceInformation({ bookingData, setBookingData }: Props) {
  const [searchTerm, setSearchTerm] = useState(bookingData.venue_name ?? "");
  const [suggestions, setSuggestions] = useState<VenueSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [fillSource, setFillSource] = useState<FillSource>(null);

  // Save venue state
  const [venueSaved, setVenueSaved] = useState(false);
  const [savingVenue, setSavingVenue] = useState(false);
  const [venueSaveError, setVenueSaveError] = useState<string | null>(null);

  // "manual" = user typed a name that returned zero results from both sources
  const needsManualEntry =
    fillSource === "manual" && searchTerm.trim().length > 0;

  // Sync if venue_name changes externally (e.g. modal reset)
  useEffect(() => {
    setSearchTerm(bookingData.venue_name ?? "");
  }, [bookingData.venue_name]);

  // Debounced dual search
  useEffect(() => {
    if (searchTerm.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const [backendResult, nominatimResult] = await Promise.allSettled([
          // ── Backend venue search ──────────────────────────────────────────
          apiService
            .get<{
              results: {
                id: number;
                name: string;
                address_one?: string;
                address_two?: string;
                suburb?: string;
                city?: string;
              }[];
            }>(`/api/venues/venue/?search=${encodeURIComponent(searchTerm)}`)
            .then((res) =>
              (res.data.results ?? []).map(
                (v): VenueSuggestion => ({
                  id: `backend-${v.id}`,
                  source: "backend",
                  displayName: v.name,
                  subtext:
                    [v.address_one, v.city].filter(Boolean).join(", ") ||
                    "In your system",
                  venue_name: v.name,
                  address_one: v.address_one ?? "",
                  address_two: v.address_two ?? "",
                  suburb: v.suburb ?? "",
                  city: v.city ?? "",
                }),
              ),
            ),

          // ── Nominatim search ──────────────────────────────────────────────
          fetch(
            `https://nominatim.openstreetmap.org/search?${new URLSearchParams({
              q: searchTerm,
              format: "json",
              addressdetails: "1",
              limit: "5",
              dedupe: "1",
              countrycodes: "zw",
            })}`,
            { headers: { "Accept-Language": "en" } },
          )
            .then((r) => r.json() as Promise<NominatimResult[]>)
            .then((data) =>
              data.map((item): VenueSuggestion => {
                const parsed = parseNominatim(item);
                return {
                  id: `nominatim-${item.place_id}`,
                  source: "nominatim",
                  displayName: parsed.venue_name,
                  subtext: item.display_name,
                  ...parsed,
                };
              }),
            ),
        ]);

        const backendItems =
          backendResult.status === "fulfilled" ? backendResult.value : [];
        const nominatimItems =
          nominatimResult.status === "fulfilled" ? nominatimResult.value : [];

        // Backend results listed first (more authoritative), then map results
        const combined: VenueSuggestion[] = [
          ...backendItems,
          ...nominatimItems,
        ];

        setSuggestions(combined);
        setShowDropdown(combined.length > 0);

        // If no results at all → flag that manual entry is needed
        if (combined.length === 0) {
          setFillSource("manual");
        }
      } catch {
        setSuggestions([]);
        setShowDropdown(false);
        setFillSource("manual");
      } finally {
        setLoading(false);
      }
    }, 450);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // User selects a suggestion → silently prefill all address fields
  const handleSelect = (item: VenueSuggestion) => {
    setSearchTerm(item.venue_name);
    setShowDropdown(false);
    setSuggestions([]);
    setFillSource(item.source);
    setVenueSaved(false);
    setVenueSaveError(null);
    setBookingData((prev) => ({
      ...prev,
      venue_name: item.venue_name,
      address_one: item.address_one,
      address_two: item.address_two,
      suburb: item.suburb,
      city: item.city,
      service_address: item.address_one,
      service_city: item.city,
    }));
  };

  // User changes the search text → clear fill state and address fields
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchTerm(val);
    setFillSource(null);
    setSuggestions([]);
    setShowDropdown(false);
    setVenueSaved(false);
    setVenueSaveError(null);
    setBookingData((prev) => ({
      ...prev,
      venue_name: val,
      address_one: "",
      address_two: "",
      suburb: "",
      city: "",
      service_address: "",
      service_city: "",
    }));
  };

  // Save the manually entered venue to the backend
  const handleSaveVenue = async () => {
    if (
      !bookingData.venue_name ||
      !bookingData.address_one ||
      !bookingData.city
    ) {
      setVenueSaveError(
        "Venue name, Address 01, and City are required to save.",
      );
      return;
    }

    setSavingVenue(true);
    setVenueSaveError(null);

    try {
      await apiService.post<{ id: number }>("/api/venues/venue/", {
        name: bookingData.venue_name,
        address_one: bookingData.address_one,
        address_two: bookingData.address_two || "",
        suburb: bookingData.suburb || "",
        city: bookingData.city,
      });

      setVenueSaved(true);
      // Flip fill source so the chip updates and the manual form locks
      setFillSource("backend");
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setVenueSaveError(detail ?? "Failed to save venue. Please try again.");
    } finally {
      setSavingVenue(false);
    }
  };

  // ── Status chip ───────────────────────────────────────────────────────────
  const statusChip = () => {
    if (!searchTerm) return null;
    if (loading) return null;

    if (venueSaved) {
      return (
        <Chip
          size="small"
          icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
          label="Venue saved — you're good to continue"
          color="success"
          variant="outlined"
          sx={{ mt: 0.75, height: 24, fontSize: 11 }}
        />
      );
    }
    if (fillSource === "backend") {
      return (
        <Chip
          size="small"
          icon={<StorefrontOutlinedIcon sx={{ fontSize: "14px !important" }} />}
          label="Address details filled in automatically"
          color="success"
          variant="outlined"
          sx={{ mt: 0.75, height: 24, fontSize: 11 }}
        />
      );
    }
    if (fillSource === "nominatim") {
      return (
        <Chip
          size="small"
          icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
          label="Location found — details filled in automatically"
          color="info"
          variant="outlined"
          sx={{ mt: 0.75, height: 24, fontSize: 11 }}
        />
      );
    }
    if (fillSource === "manual") {
      return (
        <Chip
          size="small"
          icon={<EditNoteOutlinedIcon sx={{ fontSize: "14px !important" }} />}
          label="Venue not found — enter the address below and save it"
          color="warning"
          variant="outlined"
          sx={{ mt: 0.75, height: 24, fontSize: 11 }}
        />
      );
    }
    return null;
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} mb={3}>
        Service Information
      </Typography>

      {/* ── Venue name search ──────────────────────────────────────────────── */}
      <Box sx={{ position: "relative" }}>
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
        >
          Venue Name
        </Typography>

        <Paper
          variant="outlined"
          sx={{
            display: "flex",
            alignItems: "center",
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            borderColor: showDropdown
              ? "primary.main"
              : needsManualEntry
                ? "warning.main"
                : "divider",
          }}
        >
          <SearchIcon sx={{ color: "text.disabled", mr: 1, fontSize: 18 }} />
          <InputBase
            fullWidth
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
            placeholder="Search for a venue…"
            sx={{ fontSize: 14 }}
          />
          {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </Paper>

        {statusChip()}

        {/* Suggestions dropdown */}
        {showDropdown && (
          <Paper
            elevation={4}
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 1300,
              maxHeight: 300,
              overflowY: "auto",
              mt: 0.5,
              borderRadius: 1,
            }}
          >
            <List disablePadding>
              {suggestions.map((item, index) => (
                <Box key={item.id}>
                  <ListItemButton
                    onMouseDown={() => handleSelect(item)}
                    sx={{ py: 1.25, px: 2 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {item.source === "backend" ? (
                        <StorefrontOutlinedIcon
                          fontSize="small"
                          color="success"
                        />
                      ) : (
                        <LocationOnOutlinedIcon
                          fontSize="small"
                          color="primary"
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <span>{item.displayName}</span>
                        </Box>
                      }
                      secondary={item.subtext}
                      primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                      secondaryTypographyProps={{
                        fontSize: 11,
                        noWrap: true,
                        sx: { overflow: "hidden", textOverflow: "ellipsis" },
                      }}
                    />
                  </ListItemButton>
                  {index < suggestions.length - 1 && <Divider component="li" />}
                </Box>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      {/* ── Address fields — only shown when manual entry is needed ─────────
           When a venue is found (backend or map), address is silently stored
           in bookingData but not displayed here to keep the UI clean.       */}
      {needsManualEntry && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            mt: 2,
            p: 2,
            borderRadius: 1,
            border: "1px dashed",
            borderColor: venueSaved ? "success.light" : "warning.light",
            bgcolor: venueSaved ? "success.50" : "warning.50",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              gridColumn: "1 / -1",
              color: venueSaved ? "success.dark" : "warning.dark",
              fontWeight: 500,
            }}
          >
            {venueSaved
              ? "Venue saved successfully. You can proceed with the booking."
              : "We could not find this venue automatically. Please enter the address so we can make sure the booking goes to the right place."}
          </Typography>

          <FormField
            label="Address 01 *"
            value={bookingData.address_one ?? ""}
            onChange={(v) =>
              setBookingData((prev) => ({
                ...prev,
                address_one: v,
                service_address: v,
              }))
            }
            disabled={venueSaved}
          />
          <FormField
            label="Address 02"
            value={bookingData.address_two ?? ""}
            onChange={(v) =>
              setBookingData((prev) => ({ ...prev, address_two: v }))
            }
            disabled={venueSaved}
          />
          <FormField
            label="Suburb"
            value={bookingData.suburb ?? ""}
            onChange={(v) => setBookingData((prev) => ({ ...prev, suburb: v }))}
            disabled={venueSaved}
          />
          <FormField
            label="City *"
            value={bookingData.city ?? ""}
            onChange={(v) =>
              setBookingData((prev) => ({
                ...prev,
                city: v,
                service_city: v,
              }))
            }
            disabled={venueSaved}
          />

          {/* ── Save venue button row ───────────────────────────────────── */}
          <Box
            sx={{
              gridColumn: "1 / -1",
              display: "flex",
              alignItems: "center",
              gap: 2,
              mt: 0.5,
            }}
          >
            {!venueSaved && (
              <Button
                variant="outlined"
                size="small"
                color="warning"
                onClick={handleSaveVenue}
                disabled={savingVenue}
                startIcon={
                  savingVenue ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <StorefrontOutlinedIcon fontSize="small" />
                  )
                }
                sx={{
                  textTransform: "none",
                  fontSize: 13,
                  borderRadius: "8px",
                  px: 2.5,
                }}
              >
                {savingVenue ? "Saving venue…" : "Save Venue"}
              </Button>
            )}

            {venueSaveError && (
              <Typography variant="caption" color="error">
                {venueSaveError}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* ── Service date / start time / end time ──────────────────────────── */}
      {bookingData.booking_type !== "CONFERENCE" && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
            gap: 2,
            mt: 3,
          }}
        >
          <FormField
            label="Service Date"
            value={bookingData.date_of_service ?? ""}
            onChange={(v) =>
              setBookingData((prev) => ({ ...prev, date_of_service: v }))
            }
            type="date"
          />
          <FormField
            label="Start Time"
            value={bookingData.start_time ?? ""}
            onChange={(v) =>
              setBookingData((prev) => ({ ...prev, start_time: v }))
            }
            type="time"
          />
          <FormField
            label="End Time"
            value={bookingData.end_time ?? ""}
            onChange={(v) =>
              setBookingData((prev) => ({ ...prev, end_time: v }))
            }
            type="time"
          />
        </Box>
      )}
    </Box>
  );
}
